import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { HOME_FAQS, PRICING_FAQS, FAQ_GROUPS, CONTACT_EMAIL, COMPANY, SITE_URL } from "../data/content";
import { GP_FAQS } from "../pages/GpCalculator";

const OG_IMAGE = `${SITE_URL}/og-image-v2.png`;

// Add the Google Business Profile URL (and any social profiles) here once confirmed.
const SAME_AS = [];

export const ORG_JSONLD = {
  "@type": "Organization",
  "@id": `${SITE_URL}/#organization`,
  name: "Barlo",
  legalName: COMPANY.legalName,
  url: SITE_URL,
  logo: `${SITE_URL}/icon-512.png`,
  email: CONTACT_EMAIL,
  address: {
    "@type": "PostalAddress",
    streetAddress: "2 Claremount",
    addressLocality: "Leeds",
    postalCode: "LS6 4EF",
    addressCountry: "GB",
  },
  ...(SAME_AS.length ? { sameAs: SAME_AS } : {}),
};

export const WEBSITE_JSONLD = {
  "@type": "WebSite",
  "@id": `${SITE_URL}/#website`,
  name: "Barlo",
  url: SITE_URL,
  inLanguage: "en-GB",
  publisher: { "@id": `${SITE_URL}/#organization` },
};

const offer = (name, price, unit) => ({
  "@type": "Offer",
  name,
  price,
  priceCurrency: "GBP",
  priceSpecification: { "@type": "UnitPriceSpecification", price, priceCurrency: "GBP", unitCode: unit },
});

export const SOFTWARE_APP_JSONLD = {
  "@type": "SoftwareApplication",
  name: "Barlo",
  applicationCategory: "BusinessApplication",
  operatingSystem: "Web, iOS, Android",
  url: SITE_URL,
  description: "Bar stock control and supplier ordering software for UK bars, pubs and restaurants.",
  offers: [
    offer("Starter, monthly", "29", "MON"),
    offer("Pro, monthly", "39", "MON"),
    offer("Starter, yearly", "313", "ANN"),
    offer("Pro, yearly", "421", "ANN"),
  ],
};

export function faqJsonLd(items) {
  return {
    "@type": "FAQPage",
    mainEntity: items.map((it) => ({
      "@type": "Question",
      name: it.q,
      acceptedAnswer: { "@type": "Answer", text: it.a },
    })),
  };
}

const ROUTES = {
  "/": {
    title: "Bar Stock Control Software for UK Bars and Restaurants | Barlo",
    description: "Bar stock control and supplier ordering for UK bars, pubs and restaurants. Film your stocktake, send every order in one tap and track GP. 14 days free, no card.",
    extra: [WEBSITE_JSONLD, faqJsonLd(HOME_FAQS)],
  },
  "/features": {
    title: "Bar Stocktake App Features: Ordering, GP and Waste | Barlo",
    description: "Film and count stocktakes, supplier ordering to par, cocktail recipes, price rise alerts, GP and waste reports. See what is on Starter and Pro.",
  },
  "/pricing": {
    title: "Bar Stock Control Pricing from £29 a Month | Barlo",
    description: "Starter £29 and Pro £39 a month per venue, or save 10% yearly. 14-day free trial with everything in Pro, no card needed.",
    extra: [SOFTWARE_APP_JSONLD, faqJsonLd(PRICING_FAQS)],
  },
  "/faq": {
    title: "Bar Stock Control FAQ: Stocktakes, Ordering and GP | Barlo",
    description: "Answers to common questions about bar stocktakes, supplier ordering, GP, cocktails, staff, plans and your data with Barlo.",
    extra: [faqJsonLd(FAQ_GROUPS.flatMap((g) => g.items))],
  },
  "/bar-gp-calculator": {
    title: "Free Bar GP Calculator: GP on Drinks with VAT | Barlo",
    description: "Free bar GP calculator for UK pubs, bars and restaurants. Work out cost per serve, GP % and the price you need, with VAT. No sign up.",
    extra: [faqJsonLd(GP_FAQS)],
  },
  "/about": {
    title: "About Barlo | Built by Hospitality People in Leeds",
    description: "Barlo was built after 11 years in hospitality to give independent bars and restaurants affordable stock control and supplier ordering.",
  },
  "/contact": {
    title: "Contact Barlo | Support and Enquiries",
    description: "Email hello@barloapp.co.uk for questions or more than one venue, support@barloapp.co.uk for help with the app. We reply Monday to Friday.",
  },
  "/privacy": {
    title: "Privacy Policy | Barlo",
    description: "How Barlo collects, stores and uses your data under UK GDPR, including cookies, Barlo AI and the providers we use.",
  },
  "/terms": {
    title: "Terms of Service | Barlo",
    description: "The terms for using Barlo: subscription, free trial, your data, liability and governing law.",
  },
  "/refund-policy": {
    title: "Cancellation and Billing | Barlo",
    description: "How cancelling, upgrading, downgrading and refunds work at Barlo. No contract and no exit fees.",
  },
};

const NOT_FOUND = { title: "Page not found | Barlo", description: "That page has called it a night. Head back to the bar." };

export default function SEO() {
  const { pathname } = useLocation();
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;
  const route = ROUTES[path];
  const title = route ? route.title : NOT_FOUND.title;
  const description = route ? route.description : NOT_FOUND.description;
  const url = `${SITE_URL}${path === "/" ? "/" : path}`;
  const graphJson = JSON.stringify({ "@context": "https://schema.org", "@graph": [ORG_JSONLD, ...((route && route.extra) || [])] });

  useEffect(() => {
    document.querySelectorAll("script#barlo-jsonld").forEach((el) => el.remove());
    const el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = "barlo-jsonld";
    el.textContent = graphJson;
    document.head.appendChild(el);
    return () => el.remove();
  }, [graphJson]);

  return (
    <Helmet>
      <html lang="en-GB" />
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="robots" content={route ? "index, follow, max-image-preview:large, max-snippet:-1" : "noindex, nofollow"} />
      <link rel="canonical" href={url} />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="Barlo" />
      <meta property="og:locale" content="en_GB" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={OG_IMAGE} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content="Barlo bar stock control app for UK bars and restaurants" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={OG_IMAGE} />
    </Helmet>
  );
}
